import type { PointMm } from '@besbello/design-core';

/**
 * COLOCAR UNA CAPA DENTRO DE LA ZONA: limites e imantado.
 *
 * Todo en MILIMETROS, como el resto del editor. Trabajar aqui en pixeles seria
 * atarlo al zoom y al tamano de la ventana, y una capa colocada en un movil
 * acabaria en otro sitio en un ordenador.
 *
 * Es geometria pura a proposito: sin Fabric, sin React y sin DOM. Asi se puede
 * comprobar entera con tests, que es justo lo que no se podia hacer cuando esto
 * vivia dentro del manejador de arrastre.
 */

export interface Caja {
  x: number;
  y: number;
  anchoMm: number;
  altoMm: number;
}

export interface Limites {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function limitesDe(poligono: PointMm[]): Limites {
  const xs = poligono.map((p) => p.x);
  const ys = poligono.map((p) => p.y);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

/**
 * Devuelve la caja metida dentro de la zona.
 *
 * Si la capa es MAS GRANDE que la zona no se puede encajar: se centra y se deja
 * que el aviso de «se sale» haga su trabajo. Forzarla dentro la deformaria, y
 * deformar el diseno de alguien sin avisar es peor que dejarle salirse.
 */
export function meterEnZona(caja: Caja, zona: Limites): Caja {
  const x =
    caja.anchoMm > zona.maxX - zona.minX
      ? zona.minX + (zona.maxX - zona.minX - caja.anchoMm) / 2
      : Math.min(Math.max(caja.x, zona.minX), zona.maxX - caja.anchoMm);

  const y =
    caja.altoMm > zona.maxY - zona.minY
      ? zona.minY + (zona.maxY - zona.minY - caja.altoMm) / 2
      : Math.min(Math.max(caja.y, zona.minY), zona.maxY - caja.altoMm);

  return { ...caja, x, y };
}

export interface Guia {
  /** `x` es una linea vertical; `y`, horizontal. */
  eje: 'x' | 'y';
  /** Posicion en milimetros dentro del espacio de la zona. */
  mm: number;
  motivo: 'centro-zona' | 'borde-zona' | 'centro-capa' | 'borde-capa';
}

export interface ResultadoImantado {
  caja: Caja;
  guias: Guia[];
}

/**
 * Imanta la caja a lo que tenga cerca y devuelve las guias que lo explican.
 *
 * El umbral va en MILIMETROS, no en pixeles: a un zoom del 400 % un umbral en
 * pixeles se vuelve inutil, y a un zoom del 25 % se lo traga todo.
 *
 * Los candidatos se prueban por orden de importancia —primero el centro de la
 * zona, luego sus bordes, luego las otras capas— y solo se aplica el mas
 * cercano de cada eje. Imantar a dos cosas a la vez en el mismo eje es como no
 * imantar a ninguna.
 */
export function imantar(
  caja: Caja,
  zona: Limites,
  otras: Caja[],
  umbralMm = 2,
): ResultadoImantado {
  const centroX = caja.x + caja.anchoMm / 2;
  const centroY = caja.y + caja.altoMm / 2;

  const candidatosX: Array<{ valor: number; deCaja: number; motivo: Guia['motivo'] }> = [
    { valor: (zona.minX + zona.maxX) / 2, deCaja: centroX, motivo: 'centro-zona' },
    { valor: zona.minX, deCaja: caja.x, motivo: 'borde-zona' },
    { valor: zona.maxX, deCaja: caja.x + caja.anchoMm, motivo: 'borde-zona' },
  ];
  const candidatosY: Array<{ valor: number; deCaja: number; motivo: Guia['motivo'] }> = [
    { valor: (zona.minY + zona.maxY) / 2, deCaja: centroY, motivo: 'centro-zona' },
    { valor: zona.minY, deCaja: caja.y, motivo: 'borde-zona' },
    { valor: zona.maxY, deCaja: caja.y + caja.altoMm, motivo: 'borde-zona' },
  ];

  for (const otra of otras) {
    candidatosX.push(
      { valor: otra.x + otra.anchoMm / 2, deCaja: centroX, motivo: 'centro-capa' },
      { valor: otra.x, deCaja: caja.x, motivo: 'borde-capa' },
      { valor: otra.x + otra.anchoMm, deCaja: caja.x + caja.anchoMm, motivo: 'borde-capa' },
    );
    candidatosY.push(
      { valor: otra.y + otra.altoMm / 2, deCaja: centroY, motivo: 'centro-capa' },
      { valor: otra.y, deCaja: caja.y, motivo: 'borde-capa' },
      { valor: otra.y + otra.altoMm, deCaja: caja.y + caja.altoMm, motivo: 'borde-capa' },
    );
  }

  const mejor = (candidatos: typeof candidatosX) =>
    candidatos
      .map((c) => ({ ...c, distancia: Math.abs(c.valor - c.deCaja) }))
      .filter((c) => c.distancia <= umbralMm)
      .sort((a, b) => a.distancia - b.distancia)[0];

  const mejorX = mejor(candidatosX);
  const mejorY = mejor(candidatosY);

  const guias: Guia[] = [];
  let { x, y } = caja;

  if (mejorX) {
    x += mejorX.valor - mejorX.deCaja;
    guias.push({ eje: 'x', mm: mejorX.valor, motivo: mejorX.motivo });
  }
  if (mejorY) {
    y += mejorY.valor - mejorY.deCaja;
    guias.push({ eje: 'y', mm: mejorY.valor, motivo: mejorY.motivo });
  }

  return { caja: { ...caja, x, y }, guias };
}

export type Alineacion =
  | 'izquierda'
  | 'centro-h'
  | 'derecha'
  | 'arriba'
  | 'centro-v'
  | 'abajo';

/**
 * Alinea cajas respecto a la zona (una sola caja) o entre ellas (varias).
 *
 * Con una capa seleccionada, «centrar» solo puede querer decir centrarla en la
 * zona. Con varias, quiere decir alinearlas entre si, que es lo que espera
 * cualquiera que venga de un editor grafico.
 */
export function alinear(cajas: Caja[], zona: Limites, como: Alineacion): Caja[] {
  if (cajas.length === 0) return cajas;

  const referencia: Limites =
    cajas.length === 1
      ? zona
      : {
          minX: Math.min(...cajas.map((c) => c.x)),
          minY: Math.min(...cajas.map((c) => c.y)),
          maxX: Math.max(...cajas.map((c) => c.x + c.anchoMm)),
          maxY: Math.max(...cajas.map((c) => c.y + c.altoMm)),
        };

  return cajas.map((caja) => {
    switch (como) {
      case 'izquierda':
        return { ...caja, x: referencia.minX };
      case 'centro-h':
        return { ...caja, x: (referencia.minX + referencia.maxX) / 2 - caja.anchoMm / 2 };
      case 'derecha':
        return { ...caja, x: referencia.maxX - caja.anchoMm };
      case 'arriba':
        return { ...caja, y: referencia.minY };
      case 'centro-v':
        return { ...caja, y: (referencia.minY + referencia.maxY) / 2 - caja.altoMm / 2 };
      case 'abajo':
        return { ...caja, y: referencia.maxY - caja.altoMm };
    }
  });
}

/**
 * Reparte el hueco entre tres o mas cajas.
 *
 * Con menos de tres no hay nada que repartir: la primera y la ultima marcan el
 * espacio y no se mueven.
 */
export function distribuir(cajas: Caja[], eje: 'x' | 'y'): Caja[] {
  if (cajas.length < 3) return cajas;

  const tamano = (c: Caja) => (eje === 'x' ? c.anchoMm : c.altoMm);
  const ordenadas = [...cajas].sort((a, b) => a[eje] - b[eje]);

  const primera = ordenadas[0]!;
  const ultima = ordenadas[ordenadas.length - 1]!;
  const desde = primera[eje];
  const hasta = ultima[eje] + tamano(ultima);

  const ocupado = ordenadas.reduce((suma, c) => suma + tamano(c), 0);
  const hueco = (hasta - desde - ocupado) / (ordenadas.length - 1);

  let cursor = desde;
  const colocadas = new Map<Caja, Caja>();
  for (const caja of ordenadas) {
    colocadas.set(caja, { ...caja, [eje]: cursor } as Caja);
    cursor += tamano(caja) + hueco;
  }

  // Se devuelven en el orden que llegaron: quien llama tiene sus indices.
  return cajas.map((c) => colocadas.get(c) ?? c);
}
